import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import { BracketReplacementPopover } from '@components/BlockNote/BracketReplacementPopover';
import { EditorView } from '@components/BlockNote/EditorView';
import { RoundedRectIndicator } from '@components/indicators/RoundedRectIndicator';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha, Box, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';

import type { BracketMatch } from '../../../components/BlockNoteBracketHighlightPlugin';
import { SVGIcon } from '../../../components/SVGIcon';
import {
  useSectionInteracted,
  useSectionRendered,
} from '../../../hooks/analytics/useSectionAnalytics';
import { useBracketHighlightIndicators } from '../../../hooks/useBracketHighlightIndicators';
import { indicatorRegistry } from '../../../services/indicatorRegistry';
import { getViewedIndicators, markIndicatorViewed } from '../../../services/indicatorViewedState';
import {
  buildSectionEdited,
  buildSelectionCopied,
  type TelemetryContext,
} from '../../../utils/analytics/noteTelemetryBuilder';

import { JotDocDragHandle } from './jot-doc-drag-handle';

interface JotDocSectionProps {
  /**
   * Section heading/title
   */
  heading: string;

  /**
   * Markdown content for this section
   */
  markdown: string;

  /**
   * Unique identifier for this section
   */
  sectionId: string;

  /**
   * Optional callback when markdown content changes
   */
  onMarkdownChange?: (markdown: string) => void;

  /**
   * Optional callback when the editor loses focus (blur)
   */
  onBlur?: () => void;

  /**
   * Optional callback when the section is deleted
   */
  onDelete?: () => void;

  /**
   * Optional callback when the heading changes
   */
  onHeadingChange?: (newHeading: string) => void;

  /**
   * Optional encounter ID for analytics
   */
  encounterId?: string;

  /**
   * Whether any section is currently being dragged
   */
  isDragActive?: boolean;

  /**
   * Optional callback when add section below button is clicked
   */
  onAddSectionBelow?: () => void;

  /**
   * Optional callback when BlockNote editor is ready
   */
  onEditorReady?: (editor: any) => void;

  /**
   * Optional jotdoc ID for telemetry
   */
  jotdocId?: string;

  /**
   * Optional note ID for telemetry metadata
   */
  noteId?: string;

  /**
   * Whether this section is selected for copying
   */
  isSelected?: boolean;

  /**
   * Callback to toggle section selection
   */
  onToggleSelection?: () => void;

  /**
   * Callback to append text to section content
   */
  onAppendText?: (sectionId: string, textToAppend: string) => Promise<void>;

  /**
   * Optional section order in the document (0-based index)
   * Used for ordering indicators by document position
   */
  sectionOrder?: number;

  /**
   * Whether the document is currently scrolling
   * Used to delay rendering indicators until scroll completes
   */
  isScrolling?: boolean;

  /**
   * Array of trouble words with correct spellings
   */
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>;

  /**
   * Array of misspelled words to highlight
   */
  misspelledWords?: string[];

  /**
   * Callback when a misspelled word is clicked
   */
  onWordClick?: (word: string) => void;

  /**
   * Whether the section is editable (defaults to true)
   */
  editable?: boolean;
}

/**
 * Simplified section component for JotDocs
 * Uses BlockNote editor in always-editable mode with styling similar to NoteSection
 * No AI editing, tutorials, ratings, or other extra features
 */
const JotDocSection: React.FC<JotDocSectionProps> = ({
  heading,
  markdown,
  sectionId,
  onMarkdownChange,
  onBlur,
  onDelete,
  onHeadingChange,
  encounterId,
  isDragActive = false,
  onAddSectionBelow,
  onEditorReady,
  jotdocId,
  noteId,
  isSelected = false,
  troubleWords,
  misspelledWords,
  onWordClick,
  onToggleSelection,
  onAppendText,
  sectionOrder,
  isScrolling = false,
  editable = true,
}) => {
  const theme = useTheme();
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<BlockNoteEditor | null>(null);
  const editorDOMContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionContainerRef = useRef<HTMLDivElement | null>(null);
  const headingElementRef = useRef<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditingHeading, setIsEditingHeading] = useState(false);
  const [editedHeading, setEditedHeading] = useState(heading?.replace(/\s*\([^)]*\)/g, '') || '');
  const headingInputRef = useRef<HTMLInputElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Bracket replacement popover state
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [bracketMatch, setBracketMatch] = useState<BracketMatch | null>(null);

  // Indicator registry refs for bracket indicators
  const bracketIndicatorRefs = useRef<Map<number, HTMLElement | null>>(new Map());
  // Track viewed state
  const [viewedIndicatorIds, setViewedIndicatorIds] = useState<Set<string>>(new Set());

  // Telemetry refs
  const docVersionRef = useRef<number>(0);
  const lastMarkdownRef = useRef<string>(markdown);
  const editStartedAtRef = useRef<number | null>(null);
  const lastSavedMarkdownRef = useRef<string>(markdown);
  const isInitialMountRef = useRef<boolean>(true);

  // Make section sortable
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionId,
    animateLayoutChanges: () => false, // Disable layout animations for better performance
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Initialize lastSavedMarkdownRef on mount
  useEffect(() => {
    if (isInitialMountRef.current && markdown) {
      lastSavedMarkdownRef.current = markdown;
      isInitialMountRef.current = false;
    }
  }, [markdown]);

  // Track document version for analytics (increments when markdown changes)
  useEffect(() => {
    if (lastMarkdownRef.current !== markdown) {
      docVersionRef.current += 1;
      lastMarkdownRef.current = markdown;

      // Track edit start time if content changed from saved version and we're not already tracking
      if (markdown !== lastSavedMarkdownRef.current && editStartedAtRef.current === null) {
        editStartedAtRef.current = performance.now();
      }
    }
  }, [markdown]);

  // Analytics configuration
  const analyticsDocId = useMemo(
    () => (jotdocId ? `jotdoc:${jotdocId}:section:${sectionId}` : `jotdoc-section:${sectionId}`),
    [jotdocId, sectionId],
  );
  const getDocVersion = useCallback(() => docVersionRef.current.toString(), []);
  const analyticsMetadata = useMemo(
    () => ({
      section_id: sectionId,
      note_id: noteId,
      encounter_id: encounterId,
      section_title: heading,
    }),
    [sectionId, noteId, encounterId, heading],
  );

  // Track section_rendered event (when section enters viewport)
  const sectionContext: TelemetryContext = useMemo(
    () => ({
      docId: analyticsDocId,
      getDocVersion,
    }),
    [analyticsDocId, getDocVersion],
  );
  useSectionRendered(sectionContainerRef, {
    context: sectionContext,
    getActiveSection: {
      id: sectionId,
      title: heading,
    },
    metadata: analyticsMetadata,
  });

  // Track section_interacted event (fires on mouse leave if duration >= 2 seconds)
  useSectionInteracted(sectionContainerRef, {
    context: sectionContext,
    getActiveSection: {
      id: sectionId,
      title: heading,
    },
    thresholdMs: 2000,
    metadata: analyticsMetadata,
  });

  // Expose helpers for BlockNote selection tracking
  useEffect(() => {
    const container = sectionContainerRef.current;
    if (!container) return;

    // Store helpers on container so BlockNote selection analytics can call them
    (container as any).__noteSectionTrackSelect = () => {
      if ((container as any).__sectionAnalytics?.trackTextSelected) {
        (container as any).__sectionAnalytics.trackTextSelected(sectionId);
      }
    };

    (container as any).__noteSectionTrackCopy = () => {
      if ((container as any).__sectionAnalytics?.trackTextCopied) {
        (container as any).__sectionAnalytics.trackTextCopied(sectionId);
      }
    };

    return () => {
      delete (container as any).__noteSectionTrackSelect;
      delete (container as any).__noteSectionTrackCopy;
    };
  }, [sectionId]);

  // Update editedHeading when heading prop changes
  useEffect(() => {
    setEditedHeading(heading?.replace(/\s*\([^)]*\)/g, '') || '');
  }, [heading]);

  // Focus and select input when entering edit mode
  useEffect(() => {
    if (isEditingHeading && headingInputRef.current) {
      // Use setTimeout to ensure the input is rendered before focusing
      const timer = setTimeout(() => {
        headingInputRef.current?.focus();
        headingInputRef.current?.select();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isEditingHeading]);

  // Set up blur detection using focusout event on container (same pattern as JotDocViewer)
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container || !onBlur) {
      return;
    }

    const handleBlur = (e: FocusEvent) => {
      // Don't trigger if we're editing the heading
      if (isEditingHeading) {
        return;
      }
      // Check if focus moved outside the editor container
      if (!container.contains(e.relatedTarget as Node)) {
        // Track section_edited event if content changed
        const currentMarkdown = markdown;
        const lastSaved = lastSavedMarkdownRef.current;

        if (currentMarkdown !== lastSaved) {
          const beforeLen = lastSaved.length;
          const afterLen = currentMarkdown.length;

          buildSectionEdited({
            context: sectionContext,
            beforeLen,
            afterLen,
            source: 'manual',
            timings: editStartedAtRef.current ? { startedAt: editStartedAtRef.current } : undefined,
            metadata: analyticsMetadata,
          });

          // Update lastSavedMarkdownRef to current markdown (save will happen in parent's onBlur)
          // After save completes, RTK Query will update the prop, but we've already updated our ref
          // so the next edit cycle will work correctly
          lastSavedMarkdownRef.current = currentMarkdown;
          editStartedAtRef.current = null;
        }

        onBlur();
      }
    };

    container.addEventListener('focusout', handleBlur);

    return () => {
      container.removeEventListener('focusout', handleBlur);
    };
  }, [onBlur, sectionId, isEditingHeading, markdown, sectionContext, analyticsMetadata]);

  const handleHeadingSave = () => {
    if (onHeadingChange && editedHeading.trim() && editedHeading.trim() !== heading) {
      onHeadingChange(editedHeading.trim());
    }
    setIsEditingHeading(false);
  };

  const handleHeadingKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleHeadingSave();
    } else if (e.key === 'Escape') {
      setEditedHeading(heading?.replace(/\s*\([^)]*\)/g, '') || '');
      setIsEditingHeading(false);
    }
  };

  // Combined ref callback for sortable and analytics
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (sectionContainerRef as any).current = node;
    },
    [setNodeRef],
  );

  // Track bracket highlight positions for gutter indicators
  // Use editorContainerRef (the box) as the reference container for position calculations
  const bracketIndicatorPositions = useBracketHighlightIndicators(
    editorInstanceRef.current || undefined,
    editorContainerRef,
    true, // enabled when bracket highlight is active
  );

  // Memoize bracket indicator positions relative to section (expensive DOM calculations)
  const bracketIndicatorPositionsRelativeToSection = useMemo(() => {
    if (bracketIndicatorPositions.length === 0 || !editorContainerRef.current || isScrolling) {
      return [];
    }

    const editorContainerRect = editorContainerRef.current.getBoundingClientRect();
    if (!editorContainerRect) {
      return [];
    }

    // Get the section container (parent of both gutter and editor container)
    const sectionElement = editorContainerRef.current.closest('[id^="section-"]') as HTMLElement;

    if (!sectionElement) {
      return [];
    }

    const sectionRect = sectionElement.getBoundingClientRect();

    // Calculate offset: editor container top relative to section top
    const editorContainerTopRelativeToSection = editorContainerRect.top - sectionRect.top;

    return bracketIndicatorPositions.map((position, index) => {
      // The position.top is relative to editor container, so add the offset
      const topRelativeToSection = editorContainerTopRelativeToSection + position.top;

      // Calculate line height: total height minus the dot size (12px)
      const lineHeight = Math.max(0, position.height - 12);

      return {
        ...position,
        topRelativeToSection,
        lineHeight,
        index,
      };
    });
  }, [bracketIndicatorPositions, isScrolling]);

  // Load viewed indicators on mount and when jotdocId changes
  useEffect(() => {
    if (!jotdocId) return;

    const loadViewedIndicators = async () => {
      const viewed = await getViewedIndicators(jotdocId);
      setViewedIndicatorIds(viewed);
    };

    loadViewedIndicators();
  }, [jotdocId]);

  // Handle bracket click - open popover (used by both EditorView and indicator clicks)
  const handleBracketClick = useCallback((match: BracketMatch, event: MouseEvent) => {
    setBracketMatch(match);
    setPopoverAnchor(event.target as HTMLElement);
  }, []);

  // Register/unregister bracket indicators when refs are available
  useEffect(() => {
    if (!jotdocId || sectionOrder === undefined) return;

    // Use a small delay to ensure refs are set after render
    const timeoutId = setTimeout(() => {
      bracketIndicatorPositions.forEach((position, index) => {
        const indicatorId = `bracket-${sectionId}-${index}`;
        const indicatorElement = bracketIndicatorRefs.current.get(index);

        if (indicatorElement) {
          // Calculate document position: sectionOrder * 1000 + positionInSection
          const documentPosition = sectionOrder * 1000 + index;

          // Create onActivate callback to trigger bracket click
          const onActivate = () => {
            const syntheticEvent = {
              target: position.element,
              preventDefault: () => {},
              stopPropagation: () => {},
            } as unknown as MouseEvent;
            handleBracketClick(position.match, syntheticEvent);
          };

          // Create onDeactivate callback to close bracket popover
          const onDeactivate = () => {
            setPopoverAnchor(null);
            setBracketMatch(null);
          };

          indicatorRegistry.register({
            id: indicatorId,
            type: 'bracket',
            element: indicatorElement,
            sectionId,
            documentPosition,
            onActivate,
            onDeactivate,
          });
        }
      });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      // Unregister all bracket indicators for this section
      bracketIndicatorPositions.forEach((_, index) => {
        const indicatorId = `bracket-${sectionId}-${index}`;
        indicatorRegistry.unregister(indicatorId);
      });
    };
  }, [bracketIndicatorPositions, sectionId, jotdocId, sectionOrder, handleBracketClick]);

  // Cleanup: unregister all indicators when component unmounts
  useEffect(() => {
    return () => {
      indicatorRegistry.unregisterSection(sectionId);
    };
  }, [sectionId]);

  // Handle marking indicator as viewed
  const handleMarkIndicatorViewed = useCallback(
    async (indicatorId: string) => {
      if (!jotdocId || viewedIndicatorIds.has(indicatorId)) return;

      await markIndicatorViewed(jotdocId, indicatorId);
      setViewedIndicatorIds(prev => new Set(prev).add(indicatorId));
    },
    [jotdocId, viewedIndicatorIds],
  );

  // Handle bracket replacement
  const handleBracketReplace = useCallback(
    async (replacementText: string) => {
      if (!editorInstanceRef.current || !bracketMatch) return;

      try {
        // Access the underlying TipTap/ProseMirror instance
        /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
        const tiptapEditor = (editorInstanceRef.current as any)._tiptapEditor;
        if (!tiptapEditor || !tiptapEditor.view) {
          console.warn('BlockNote editor TipTap instance not available');
          return;
        }

        const { state, dispatch } = tiptapEditor.view;
        const { tr } = state;

        // Replace the bracket text with the replacement text (without brackets)
        // If replacement is empty, remove the bracket entirely
        // Otherwise, use the replacement text as-is (no brackets)
        const trimmedReplacement = replacementText.trim();
        const replacement = trimmedReplacement;

        tr.replaceWith(bracketMatch.from, bracketMatch.to, state.schema.text(replacement));
        dispatch(tr);
        /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

        // Get updated markdown from editor and save
        if (onAppendText && editorInstanceRef.current) {
          try {
            const updatedMarkdown = await editorInstanceRef.current.blocksToMarkdownLossy(
              editorInstanceRef.current.document,
            );
            await onAppendText(sectionId, updatedMarkdown);
          } catch (saveError) {
            console.error('Error saving bracket replacement:', saveError);
          }
        }

        // Close popover
        setPopoverAnchor(null);
        setBracketMatch(null);
      } catch (error) {
        console.error('Error replacing bracket text:', error);
      }
    },
    [bracketMatch, onAppendText, sectionId],
  );

  const handleBracketPopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setBracketMatch(null);
  }, []);

  return (
    <Box
      id={`section-${sectionId}`}
      ref={combinedRef}
      style={style}
      sx={{ scrollMarginTop: '4em', position: 'relative', pl: 8 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Left gutter column for buttons - positioned absolutely */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '64px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {/* Bracket highlight indicators */}
        {bracketIndicatorPositionsRelativeToSection.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 4,
              top: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'auto',
              zIndex: 1,
            }}
          >
            {bracketIndicatorPositionsRelativeToSection.map(
              ({ topRelativeToSection, lineHeight, element, match, index }) => {
                const indicatorId = `bracket-${sectionId}-${index}`;
                const isViewed = viewedIndicatorIds.has(indicatorId);

                return (
                  <Box
                    key={index}
                    ref={(el: HTMLElement | null) => {
                      bracketIndicatorRefs.current.set(index, el);
                    }}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: `${topRelativeToSection}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      opacity: isViewed ? 0.4 : 1,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                    onClick={e => {
                      // Mark as viewed
                      handleMarkIndicatorViewed(indicatorId);
                      // Create a synthetic MouseEvent-like object for handleBracketClick
                      // Use the bracket element as the anchor so popover appears at bracket location
                      const syntheticEvent = {
                        target: element,
                        preventDefault: () => e.preventDefault(),
                        stopPropagation: () => e.stopPropagation(),
                      } as unknown as MouseEvent;
                      handleBracketClick(match, syntheticEvent);
                    }}
                    onMouseEnter={() => {
                      // Mark as viewed on hover
                      handleMarkIndicatorViewed(indicatorId);
                      // Highlight the corresponding bracket element on hover
                      element.style.backgroundColor = '#BBDEFB';
                    }}
                    onMouseLeave={() => {
                      // Restore original highlight color
                      element.style.backgroundColor = '#E3F2FD';
                    }}
                  >
                    {/* Dot at the top */}
                    <RoundedRectIndicator
                      width={12}
                      height={12}
                      borderRadius="50%"
                      bgcolor="#E3F2FD"
                      borderColor="#90CAF9"
                      hoverBgcolor="#BBDEFB"
                      hoverBorderColor="#64B5F6"
                      hoverScale={1.15}
                      sx={{ cursor: 'pointer' }}
                    />
                    {/* Vertical line extending down */}
                    {lineHeight > 0 && (
                      <Box
                        sx={{
                          width: '1px',
                          height: `${lineHeight}px`,
                          bgcolor: '#E0E0E0',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      />
                    )}
                  </Box>
                );
              },
            )}
          </Box>
        )}

        {/* Copy button - visible on hover */}
        <Box
          sx={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title={copied ? 'Copied!' : 'Copy section'}>
            <IconButton
              size="small"
              onClick={async e => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(markdown);
                  setCopied(true);

                  // Track copy event
                  buildSelectionCopied({
                    context: sectionContext,
                    lenSelected: markdown.length,
                    lenTotal: markdown.length,
                    modality: 'copy_button',
                    metadata: analyticsMetadata,
                  });

                  setTimeout(() => {
                    setCopied(false);
                  }, 1000);
                } catch (err) {
                  console.error('Failed to copy section:', err);
                }
              }}
              sx={{
                color: 'textColors.darkText',
                transition: 'opacity 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'textColors.darkText',
                },
              }}
            >
              {copied ? (
                <CheckIcon fontSize="small" sx={{ transition: 'opacity 0.2s ease-in-out' }} />
              ) : (
                <ContentCopyIcon fontSize="small" sx={{ transition: 'opacity 0.2s ease-in-out' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        {/* Select button - visible on hover */}
        {onToggleSelection && (
          <Box
            sx={{
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title={isSelected ? 'Deselect section' : 'Select section for copying'}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onToggleSelection();
                }}
                sx={{
                  color: isSelected ? 'textColors.darkText' : 'textColors.disabled',
                  opacity: isSelected ? 1 : 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'textColors.darkText',
                  },
                }}
              >
                <SVGIcon name={isSelected ? 'check-filled' : 'check-unfilled'} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Drag handle - visible on hover */}
        <Box
          sx={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Drag to reorder">
            <Box>
              <JotDocDragHandle listeners={listeners} attributes={attributes} />
            </Box>
          </Tooltip>
        </Box>
        {onDelete && (
          <Box
            sx={{
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title="Delete section">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setIsDeleteDialogOpen(true);
                }}
                sx={{
                  color: 'textColors.disabled',
                  opacity: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'textColors.disabled',
                    opacity: 0.8,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Add section below button */}
        <Box
          sx={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Add section below">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                if (onAddSectionBelow) {
                  onAddSectionBelow();
                }
              }}
              sx={{
                color: 'textColors.disabled',
                opacity: 0.5,
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'textColors.disabled',
                  opacity: 0.8,
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Heading row: heading text with compliance rule indicators */}
      <Box sx={{ marginBottom: 1 }}>
        <Box
          sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}
          id="jotdoc-section-heading"
          className="flex items-center"
        >
          {isEditingHeading ? (
            <TextField
              inputRef={headingInputRef}
              value={editedHeading}
              onChange={e => setEditedHeading(e.target.value)}
              onBlur={handleHeadingSave}
              onKeyDown={handleHeadingKeyDown}
              variant="standard"
              autoFocus
              fullWidth
              sx={{
                width: '100%',
                '& .MuiInput-underline:before': {
                  borderBottomColor: 'primary.main',
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottomColor: 'primary.dark',
                },
              }}
             slotProps={{ input: {
                sx: {
                  fontWeight: 'bold',
                  color: 'textColors.darkText',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  textTransform: 'capitalize',
                },
              } }}/>
          ) : (
            <Typography
              ref={headingElementRef}
              variant="h6"
              onClick={() => {
                if (onHeadingChange) {
                  setIsEditingHeading(true);
                }
              }}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 'bold',
                color: 'textColors.darkText',
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                cursor: onHeadingChange ? 'pointer' : 'default',
                userSelect: 'none',
                '&:hover': onHeadingChange
                  ? {
                      textDecoration: 'underline',
                      textDecorationColor: 'primary.main',
                    }
                  : {},
              }}
            >
              {/* Remove any text between parentheses, e.g. (Customized) */}
              {heading?.replace(/\s*\([^)]*\)/g, '')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Content box - aligned with heading text */}
      <Box sx={{ width: '100%' }}>
        <Box
          ref={editorContainerRef}
          sx={{
            pt: 2,
            pb: 2,
            mb: 4,
            border: 1,
            borderRadius: '0.75rem',
            color: 'textColors.darkText',
            borderColor: 'borderColors.primary',
            bgcolor: 'backgroundColors.primary',
            position: 'relative',
            overflow: 'hidden',
            ...(isDragActive && {
              maxHeight: '200px',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '40px',
                background: `linear-gradient(to bottom, transparent 0%, ${alpha(
                  theme.palette.backgroundColors?.primary || theme.palette.background.default,
                  0.5,
                )} 50%, ${theme.palette.backgroundColors?.primary || theme.palette.background.default} 100%)`,
                pointerEvents: 'none',
                zIndex: 1,
              },
            }),
            // Hide BlockNote drag handles and side menu
            '& .bn-side-menu': {
              display: 'none !important',
            },
            '& .bn-drag-handle-menu': {
              display: 'none !important',
            },
            '& .bn-block-handle': {
              display: 'none !important',
            },
            '& [data-node-type]': {
              '&:hover .bn-side-menu': {
                display: 'none !important',
              },
              '&:hover .bn-drag-handle-menu': {
                display: 'none !important',
              },
            },
          }}
        >
          <EditorView
            markdown={markdown}
            editable={editable}
            enableBracketHighlight={true}
            enableMisspelling={!!misspelledWords && misspelledWords.length > 0}
            misspelledWords={misspelledWords}
            onWordClick={onWordClick}
            troubleWords={troubleWords}
            onMarkdownChange={onMarkdownChange}
            onBracketClick={handleBracketClick}
            onEditorReady={editor => {
              editorInstanceRef.current = editor;
              if (onEditorReady) {
                onEditorReady(editor);
              }
            }}
            onEditorContainerRef={container => {
              editorDOMContainerRef.current = container;
            }}
            styleId={`jotdoc-section-editor-${sectionId}`}
            analytics={{
              enabled: true,
              docId: analyticsDocId,
              getDocVersion,
              metadata: analyticsMetadata,
            }}
          />
        </Box>
      </Box>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        variant="warning"
        title="Delete section?"
        message="Are you sure you want to delete this section? This action cannot be undone."
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          if (onDelete) {
            onDelete();
          }
        }}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Bracket replacement popover */}
      <BracketReplacementPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        bracketMatch={bracketMatch}
        onClose={handleBracketPopoverClose}
        onReplace={handleBracketReplace}
      />
    </Box>
  );
};

export default JotDocSection;
